import {
  Button,
  HighlightIcon,
  Icon,
  ModalBody,
  Avatar,
  DATE_FORMAT,
  Tooltip,
} from '@dev-spendesk/grapes';
import React, { useEffect } from 'react';

import { useFeature } from 'common/hooks/useFeature';
import { useTranslation } from 'common/hooks/useTranslation';
import { capitalize } from 'common/utils/string';
import { type Company } from 'modules/app/hooks/useCompany';
import { onMemberDetailsModalOpened as trackOnMemberDetailsModalOpened } from 'modules/members/tracking';
import FEATURES from 'src/core/constants/features';
import { type AccountingSoftware } from 'src/core/modules/bookkeep/integration/status';
import { useHasWireTransferDelegationFeature } from 'src/core/modules/delegation/hooks/useHasWireTransferDelegationFeature';
import { useIsUserManagedByKombo } from 'src/core/modules/integrations/hooks';

import { MemberDetailsRow as Row } from './MemberDetailsModalRow';
import { MemberDetailsModalControlRule } from './components/MemberDetailsModalControlRule';
import { MemberDetailsModalPolicy } from './components/MemberDetailsModalPolicy';
import {
  getMemberName,
  getMemberPhoneNumber,
  getMemberTeams,
  getMemberRoleNamesForDisplay,
  type MemberDetails,
} from '../../models/member';
import { MemberSyncStatus } from '../MemberSyncStatus/MemberSyncStatus';

import './MemberDetailsModal.css';

type MemberSyncData = {
  isEnabled: boolean;
  integrationName?: AccountingSoftware;
  status?: { synced: boolean; url: string | null };
};

type Props = {
  member: MemberDetails;
  company: Company;
  isCurrentUser: boolean;
  memberSync?: MemberSyncData;
  onEdit(): void;
  onDelete(): void;
  onResendInvitation(): void;
  onUpdateCardsAccess(hasAccess: boolean): Promise<void>;
};

export function MemberDetailsModal({
  member,
  company,
  isCurrentUser,
  memberSync,
  onEdit,
  onDelete,
  onResendInvitation,
  onUpdateCardsAccess,
}: Props) {
  useEffect(() => {
    trackOnMemberDetailsModalOpened({ member });
  }, []);

  const { t } = useTranslation('global');

  const isTeamsFeatureEnabled = useFeature(FEATURES.TEAMS);
  const isCostCentersFeatureEnabled = useFeature(
    FEATURES.COST_CENTERS_ACTIVATED,
  );
  const isPlayByRulesFeatureEnabled = useFeature(FEATURES.PLAY_BY_RULES);
  const isCardsAccessFeatureEnabled = useFeature(FEATURES.CARDS_ACCESS);

  const hasReportingManagerFeature = useFeature(FEATURES.REPORTING_MANAGERS);
  const hasWireTransferConfirmationFeature =
    useHasWireTransferDelegationFeature();

  const isCardManuallyBlocked = isCardsAccessManuallyRestricted(member);

  const isManagedByKombo = useIsUserManagedByKombo({ userId: member.id });

  const translatedRoleNames = getMemberRoleNamesForDisplay(member, t).map(
    capitalize,
  );
  const memberTeams = getMemberTeams(member);

  const isValidBankInfo =
    Array.isArray(member?.requiredInfoTypesForEmployeeBankAddress) &&
    member.requiredInfoTypesForEmployeeBankAddress.every(
      (key) => member.bankInfo?.[key] !== undefined,
    );

  const bankInfoMessage = () => {
    if (!member.bankInfo) {
      return (
        <div className="flex items-center justify-between">
          {t('members.detailsModal.noBankInfos')}
          <Icon name="triangle-warning" size="s" />
        </div>
      );
    }

    if (isValidBankInfo) {
      return (
        <div className="text-content-secondary-bg-secondary">
          {t('members.bankInfoCorrect')}
        </div>
      );
    }

    return (
      <div className="flex items-center justify-between">
        {t('members.detailsModal.notSupportedBankInfos')}
        <Icon name="triangle-warning" size="s" />
      </div>
    );
  };

  return (
    <ModalBody>
      <div className="my-24 text-left">
        <MemberDetailsModalHeader member={member} memberSync={memberSync} />
        <div className="separator my-16" />

        <div className="pb-8">
          <div className="MemberDetailsModal__main__heading title-l">
            {t('members.detailsModal.memberInformation')}
            <Button
              variant="primaryBrand"
              text={t('members.detailsModal.editButton')}
              onClick={() => onEdit()}
            />
          </div>

          <Row label={t('misc.emailAddress')}>
            <div className="text-content-secondary-bg-secondary" data-mask>
              {member.email}
            </div>
          </Row>

          <Row label={t('misc.phoneNumber')}>
            {!member.mobileNo || !member.mobileExt ? (
              <div className="text-content-secondary-bg-secondary">
                {t('members.detailsModal.noMobileNumber')}
              </div>
            ) : (
              <div className="text-content-secondary-bg-secondary" data-mask>
                {getMemberPhoneNumber(member)}
              </div>
            )}
          </Row>

          <Row label={t('misc.roles')}>
            {translatedRoleNames.length === 0 ? (
              <div className="text-content-secondary-bg-secondary">
                {t('members.memberHasNoRole')}
              </div>
            ) : (
              <div className="text-content-secondary-bg-secondary">
                {translatedRoleNames.join(', ')}
              </div>
            )}
          </Row>
          {hasReportingManagerFeature && (
            <Row label={t('members.detailsModal.manager')}>
              <div className="text-content-secondary-bg-secondary" data-mask>
                {member.manager
                  ? member.manager.fullNameOrEmail
                  : t('members.detailsModal.noManager')}
              </div>
            </Row>
          )}

          {hasWireTransferConfirmationFeature && (
            <Row label={t('members.detailsModal.additionalRights')}>
              {member.canConfirmPayments ? (
                <div className="text-content-secondary-bg-secondary">
                  {t('members.detailsModal.confirmPayments')}
                </div>
              ) : (
                <div className="text-content-secondary-bg-secondary">
                  {t('members.detailsModal.noAdditionalRights')}
                </div>
              )}
            </Row>
          )}

          {isCostCentersFeatureEnabled && (
            <Row label={t('members.detailsModal.costCenter')}>
              {!member.costCenter ? (
                <div className="text-content-secondary-bg-secondary">
                  {t('members.detailsModal.noCostCenter')}
                </div>
              ) : (
                <div className="text-content-secondary-bg-secondary">
                  {member.costCenter?.name}

                  {member.costCenter.selectionMode === 'suggested' && (
                    <span> ({t('members.suggestedCostCenter')})</span>
                  )}
                </div>
              )}
            </Row>
          )}

          {isPlayByRulesFeatureEnabled && (
            <Row label={t('members.detailsModal.controlRule')}>
              <MemberDetailsModalControlRule controlRule={member.controlRule} />
            </Row>
          )}

          {isTeamsFeatureEnabled && (
            <Row label={t('misc.teams')}>
              {memberTeams.length === 0 ? (
                <div className="text-content-secondary-bg-secondary">
                  {t('members.memberHasNoGroup')}
                </div>
              ) : (
                <div className="text-content-secondary-bg-secondary">
                  {memberTeams.map((team) => team.name).join(', ')}
                </div>
              )}
            </Row>
          )}

          <Row label={t('misc.policy')}>
            <MemberDetailsModalPolicy member={member} company={company} />
          </Row>

          {company.type !== 'branch_expense_entity' && (
            <Row
              label={t('members.bankInfo')}
              containerClassNames={
                member.bankInfo && isValidBankInfo
                  ? ''
                  : 'MemberDetailsModal__warningContainer'
              }
            >
              {bankInfoMessage()}
            </Row>
          )}
        </div>
        <div className="separator my-16" />

        <div className="MemberDetailsModal__actions">
          {member.isPending && (
            <div className="flex items-center gap-16">
              <Button
                iconName="bell"
                variant="secondaryNeutral"
                text={t('members.resendInvitation')}
                onClick={() => onResendInvitation()}
              />
              <Tooltip
                content={t(
                  'integration.members.invitationForHrManagedMemberCantBeRevokedTooltip',
                )}
                placement="top"
                isDisabled={!isManagedByKombo}
                maxWidth={320}
              >
                <Button
                  iconName="circle-minus"
                  variant="secondaryNeutral"
                  text={t('members.revokeInvitation')}
                  onClick={() => onDelete()}
                  isDisabled={isManagedByKombo}
                />
              </Tooltip>
            </div>
          )}

          {!member.isPending &&
            !isCurrentUser &&
            !member.isAccountOwner &&
            !member.isOrganisationOwner && (
              <div className="flex items-center gap-16">
                {isCardsAccessFeatureEnabled && (
                  <Button
                    variant="secondaryNeutral"
                    iconName={
                      isCardManuallyBlocked ? 'lock-close' : 'lock-open'
                    }
                    text={
                      isCardManuallyBlocked
                        ? t('members.detailsModal.allowCardsAccess')
                        : t('members.detailsModal.restrictCardsAccess')
                    }
                    onClick={() => onUpdateCardsAccess(isCardManuallyBlocked)}
                  />
                )}
                <Tooltip
                  content={t(
                    'integration.members.hrManagedMemberCantBeDeletedTooltip',
                  )}
                  placement="top"
                  isDisabled={!isManagedByKombo}
                  maxWidth={320}
                >
                  <Button
                    iconName="circle-minus"
                    variant="secondaryAlert"
                    text={t('members.detailsModal.removeMemberButton')}
                    onClick={onDelete}
                    isDisabled={isManagedByKombo}
                  />
                </Tooltip>
              </div>
            )}
          {member.isAccountOwner && (
            <div className="MemberDetailsModal__actions_ao-cant-be-deleted text-content-primary">
              {t('members.detailsModal.aoCantBeDeleted')}
            </div>
          )}
          {member.isOrganisationOwner && (
            <div className="MemberDetailsModal__actions_ao-cant-be-deleted text-content-primary">
              {t('members.detailsModal.ooCantBeDeleted')}
            </div>
          )}
          {!member.isAccountOwner &&
            !member.isOrganisationOwner &&
            isCurrentUser && (
              <div className="MemberDetailsModal__actions_ao-cant-be-deleted text-content-primary">
                {t('members.detailsModal.cantDeleteOwnAccount')}
              </div>
            )}
        </div>
      </div>
    </ModalBody>
  );
}

const MemberDetailsModalHeader = ({
  member,
  memberSync,
}: {
  member: MemberDetails;
  memberSync?: MemberSyncData;
}) => {
  const { t, localeFormat } = useTranslation('global');

  return (
    <div className="flex flex-col content-stretch items-center gap-8">
      <Avatar src={member.avatar} text={getMemberName(member)} size={56} />

      <div id="MemberDetailsModal" className="mt-8 flex items-center gap-8">
        <h1 className="text-content-primary heading-l leading-[24px]" data-mask>
          {getMemberName(member)}
        </h1>
        {isCardsAccessManuallyRestricted(member) && (
          <HighlightIcon size={24} variant="alert" name="card-lock-close" />
        )}
      </div>

      {memberSync?.isEnabled && memberSync.status && (
        <MemberSyncStatus
          status={memberSync.status}
          memberName={getMemberName(member)}
          integrationName={memberSync.integrationName}
        />
      )}

      <div className="flex items-center justify-center gap-8">
        <p className="text-content-secondary-bg-primary">
          {t(member.isPending ? 'members.invitedOn' : 'members.memberSince', {
            date: localeFormat(new Date(member.createdAt), DATE_FORMAT.SHORT),
          })}
        </p>
        <div className="border-border-default h-[16px] w-0 border border-solid" />
        <p className="text-content-secondary-bg-primary">
          {t('members.countPlasticCards', {
            count: member.plasticCard ? 1 : 0,
          })}
        </p>
        <div className="border-border-default h-[16px] w-0 border border-solid" />
        <p className="text-content-secondary-bg-primary">
          {t('members.countActiveSubscriptions', {
            count: member.subscriptionsCount,
          })}
        </p>
      </div>
    </div>
  );
};

const isCardsAccessManuallyRestricted = ({ cardsAccess }: MemberDetails) =>
  cardsAccess?.hasAccess === false && cardsAccess.initiator === 'user';
